import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { Suspense } from 'react';

import AudioVisualizerCanvas from '0_canvas/AudioVisualizerCanvas/AudioVisualizerCanvas';
import ClientCanvas from '0_canvas/ClientCanvas/ClientCanvas';
import CloudOverlayScene from '0_canvas/Scenes/CloudOverlayScene/CloudOverlayScene';
import MainScene from '0_canvas/Scenes/MainScene/MainScene';
import AudioRouting from '1_components/AudioRouting/AudioRouting';
import { ExperienceEnabled } from '1_components/ExperienceEnabled/ExperienceEnabled';
import FoucWrapper from '1_components/FoucWrapper/FoucWrapper';
import GoogleTagLinkClicksObserver from '1_components/GoogleTagLinkClicksObserver/GoogleTagLinkClicksObserver';
import FacebookPixelTracker from '1_components/MetaPixelTracker/MetaPixelTracker';
import NoPreloadedFallback from '1_components/NoPreloadedFallback/NoPreloadedFallback';
import RedirectOnPopstate from '1_components/RedirectOnPopstate/RedirectOnPopstate';
import SaveHistory from '1_components/SaveHistory/SaveHistory';
import AudioController from '2_sections/AudioController/AudioController';
import Footer from '2_sections/Footer/Footer';
import GlitchTransition from '2_sections/GlitchTransition/GlitchTransition';
import Header from '2_sections/Header/Header';
import { SideMenu } from '2_sections/Header/SideMenu/SideMenu';
import ModalContainer from '2_sections/ModalContainer/ModalContainer';
import {
  gtWalsheim,
  spaceGrotesk,
} from '2_sections/RootLayout/RootLayout.fonts';
import S from '2_sections/RootLayout/RootLayout.module.scss';
import RotateDevice from '2_sections/RotateDevice/RotateDevice';
import Atmos from '2_sections/Sfx/Atmos';
import CanvasAudioSetup from '2_sections/Sfx/CanvasAudioSetup';
import MainMusic from '2_sections/Sfx/MainMusic';
import { publicAssets } from 'utils/gcs';

import 'styles/globalStyles.scss';
import 'styles/fonts.scss';

import { FB_PIXEL_ID } from 'lib/fpixel';

const metaPixel = `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${FB_PIXEL_ID}');
            `;

const SOCIAL_PREVIEW = publicAssets('/social-preview.jpg');

/**
 * Everything rendered here sits outside `<Component />` and therefore keeps its
 * instance across route changes. That is the whole point for the WebGL
 * canvases below: they hold a GL context and a fully built 3D scene, and
 * rebuilding either on navigation is what made the globe visibly restart.
 */
export default function App({ Component, pageProps }: AppProps) {
  const { asPath } = useRouter();

  return (
    <div
      className={clsx(spaceGrotesk.variable, gtWalsheim.variable)}
      style={{ display: 'contents' }}
    >
      <Head>
        <title>Haven</title>
        <meta
          name="description"
          content="Your home is trying to tell you something"
        />
        <meta name="robots" content="index, follow" />
        <meta
          name="keywords"
          content="Resilience Road, Suncorp Resilience Road, Onehouse, Suncorp Onehouse"
        />
        <meta
          name="tags"
          content="Resilience Road, Suncorp Resilience Road, Onehouse, Suncorp Onehouse"
        />

        <meta property="og:title" content="Haven" key="og:title" />
        <meta
          property="og:description"
          content="Your home is trying to tell you something"
          key="og:description"
        />
        <meta
          property="og:url"
          content="https://haven.suncorp.com.au"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta property="og:image" content={SOCIAL_PREVIEW} key="og:image" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Suncorp Onehouse" />

        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitter:card"
        />
        <meta name="twitter:title" content="Haven" key="twitter:title" />
        <meta
          name="twitter:description"
          content="Your home is trying to tell you something"
          key="twitter:description"
        />
        <meta
          name="twitter:image"
          content={SOCIAL_PREVIEW}
          key="twitter:image"
        />
      </Head>

      <Script id="fb-pixel" strategy="afterInteractive">
        {metaPixel}
      </Script>

      <ClientCanvas>
        <MainScene />
      </ClientCanvas>

      <ClientCanvas camera={{ position: [0, 0, -100] }} isOverLayout>
        <CloudOverlayScene />
      </ClientCanvas>

      <AudioVisualizerCanvas />

      <Suspense>
        <FacebookPixelTracker />
        <GoogleTagLinkClicksObserver />
        <RedirectOnPopstate />
        <SaveHistory />
        <NoPreloadedFallback />

        <Header sideMenu={<SideMenu />} />
        <main className={S.Main}>
          <ExperienceEnabled>
            <AudioRouting />
          </ExperienceEnabled>

          <FoucWrapper>
            {/*
              Page transitions are driven entirely by AnimatePresence keyed on
              the route: the outgoing page stays mounted until its exit
              animation finishes, then the next one enters. Nothing here
              unmounts the shell around it, so the canvases keep their context.
            */}
            <AnimatePresence
              mode="wait"
              onExitComplete={() => window.scrollTo({ top: 0 })}
            >
              <motion.div
                key={`page${asPath}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
              >
                <Component {...pageProps} />
              </motion.div>
            </AnimatePresence>
          </FoucWrapper>
          <GlitchTransition />
          <Footer />

          <AudioController />
          <MainMusic />
          <Atmos />
          <CanvasAudioSetup audioNames={['general_clouds_long']} />

          <ModalContainer />
          <RotateDevice />
        </main>
      </Suspense>
    </div>
  );
}
